import { useEffect, useMemo } from 'react'

import {
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  format,
  parse,
} from 'date-fns'
import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'
import { useSearchParams } from 'react-router-dom'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { PageHeader } from '@/components/layout/page/page-header.tsx'
import { Page } from '@/components/layout/page/page.tsx'
import {
  DateRangeContextKey,
  useDateRangeContext,
} from '@/components/provider/date-range-provider.tsx'
import { Button } from '@/components/ui/button.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { getDateInTimezone } from '@/components/ui/calendar/utils'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $account } from '@/models/account.ts'
import { useAccountingData } from '@/modules/accounting/hooks/useAccountingData.ts'
import type { AccountingSnapshotItem } from '@/modules/accounting/types'
import { AccountingCostsChart } from '@/modules/accounting/ui/AccountingCostsChart.tsx'
import { AccountingPageSkeleton } from '@/modules/accounting/ui/AccountingPageSkeleton.tsx'
import { AccountingProfitChart } from '@/modules/accounting/ui/AccountingProfitChart.tsx'
import { AccountingAdditionalCostsTable } from '@/modules/accounting/ui/AdditionalCosts/AccountingAdditionalCostsTable.tsx'
import { AccountingCreatorsTable } from '@/modules/accounting/ui/creators/AccountingCreatorsTable.tsx'
import { AccountingMembersTable } from '@/modules/accounting/ui/members/AccountingMembersTable.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { DateUnit, formatDateByUnit } from '@/utils/date'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

const PAST_YEARS_LIMIT = 2

export const AccountingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { today } = useDateRangeContext()
  const account = useUnit($account)
  const { userSettings } = useAccount()
  const shouldShowCents = !!userSettings?.showCents
  const { date, setDate } = useDateRangeContext(DateRangeContextKey.Accounting)
  const toast = useToast()
  const formattedDateRange =
    date.from &&
    date.to &&
    `${format(date.from, 'MMM yy')} - ${format(date.to, 'MMM yy')}`

  useEffect(() => {
    trackFeatureUsage(TrackedFeatures.Accounting)
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    if (from && to) {
      const parsedFrom = parse(from, 'yyyy-MM-dd', new Date())
      const parsedTo = parse(to, 'yyyy-MM-dd', new Date())

      setDate({
        from: getDateInTimezone(account?.timezone, parsedFrom),
        to: getDateInTimezone(account?.timezone, parsedTo),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { accountingData, isLoading: isAccountingDataLoading } =
    useAccountingData({
      from: date.from,
      to: date.to,
    })

  const handleDateChange = (dateRange: DateRange) => {
    if (dateRange.from && dateRange.to) {
      setDate(dateRange)
      setSearchParams({
        from: format(dateRange.from, 'yyyy-MM-dd'),
        to: format(dateRange.to, 'yyyy-MM-dd'),
      })
    } else {
      toast.warning('Please select from the start to the end of a month')
    }
  }

  const profitChartData: AccountingSnapshotItem[] = useMemo(() => {
    if (date.from && date.to) {
      const unit = accountingData?.dailyStatistics
        ? DateUnit.Daily
        : DateUnit.Monthly
      const dateItems =
        unit === DateUnit.Monthly
          ? eachMonthOfInterval({ start: date.from, end: date.to })
          : eachDayOfInterval({ start: date.from, end: date.to })

      const data =
        accountingData?.dailyStatistics || accountingData?.monthlyStatistics

      if (!data) return []

      return dateItems.map((item, index) => {
        const date =
          unit === DateUnit.Monthly
            ? format(item, 'yyyy-MM')
            : format(item, 'yyyy-MM-dd')

        const totalRevenue = data.totalRevenue?.[date] ?? 0

        return {
          id: index,
          date: formatDateByUnit(item, unit),
          totalRevenue,
          netProfit: data.totalAgencyProfit?.[date] ?? 0,
          creatorCosts: data.creatorCost?.[date] ?? 0,
          memberCosts: data.teamMemberCost?.[date] ?? 0,
          additionalCosts: data.additionalCosts?.[date] ?? 0,
        }
      })
    }

    return []
  }, [accountingData, date.from, date.to])

  if (isAccountingDataLoading) {
    return <AccountingPageSkeleton />
  }

  return (
    <Page>
      <PageHeader
        title="Accounting"
        optionComponents={
          <>
            <CalendarRangeDatePicker
              maxDateRange={{
                to: today,
                from: addYears(today, -PAST_YEARS_LIMIT),
              }}
              date={date}
              setDate={handleDateChange}
            />
            <Tooltip
              content="To ensure consistency with OnlyFans statements, all data on the Accounting page is calculated in UTC (GMT+0), regardless of your personal settings."
              contentClassName="max-w-[280px] text-left"
              className="self-center"
              side="bottom"
            >
              <InfoIcon className="h-6 w-6" />
            </Tooltip>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <AccountingProfitChart
          accountingSnapshotItems={profitChartData ?? []}
          showCents={shouldShowCents}
        />

        <AccountingCostsChart
          accountingSnapshotItems={profitChartData ?? []}
          showCents={shouldShowCents}
        />
      </div>
      <div className="mt-6">
        <AccountingCreatorsTable
          creatorsData={accountingData?.creatorStatistics ?? []}
          showCents={shouldShowCents}
          formattedRange={formattedDateRange}
        />
      </div>
      <div className="mt-6">
        <AccountingMembersTable
          membersData={accountingData?.teamMemberStatistics ?? []}
          showCents={shouldShowCents}
          formattedRange={formattedDateRange}
        />
      </div>
      {date.from && date.to && (
        <div className="mt-12">
          <AccountingAdditionalCostsTable
            from={date.from}
            to={date.to}
            showCents={shouldShowCents}
          />
        </div>
      )}
    </Page>
  )
}
